import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import {
  AlertCircle, Loader, BarChart2, Users, Eye, Clock, Globe,
  Smartphone, Monitor, Tablet, RefreshCw, Link2, Unlink, ChevronDown,
  TrendingUp, TrendingDown, ArrowRight, ExternalLink, CheckCircle, MapPin
} from 'lucide-react';

const GoogleAnalytics = ({ user }) => {
  const { selectedSite, sites, loading: sitesLoading, selectSite } = useSiteContext();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [showPropertySelect, setShowPropertySelect] = useState(false);
  const [dateRange, setDateRange] = useState('7d');

  useEffect(() => {
    if (selectedSite) {
      checkStatus();
    } else {
      setLoading(false);
      setError('Please select a site');
    }
  }, [selectedSite]);

  useEffect(() => {
    // Check for callback params in URL
    const hash = window.location.hash;

    if (hash.includes('error=')) {
      const errorMatch = hash.match(/error=([^&]+)/);
      if (errorMatch) {
        setError(decodeURIComponent(errorMatch[1]));
      }
      window.location.hash = 'google-analytics';
      return;
    }

    if (hash.includes('connected=true')) {
      // Wait for sites to load before checking status
      if (sitesLoading) return;

      // Try to get siteId from URL and select that site
      const siteIdMatch = hash.match(/siteId=(\d+)/);
      if (siteIdMatch && sites.length > 0) {
        const siteId = parseInt(siteIdMatch[1]);
        const site = sites.find(s => s.id === siteId);
        if (site && (!selectedSite || selectedSite.id !== siteId)) {
          selectSite(site);
        }
      }

      // Clear the URL params
      window.location.hash = 'google-analytics';

      // Only check status if we have a selected site
      if (selectedSite) {
        checkStatus();
      }
    }
  }, [sitesLoading, sites, selectedSite]);

  const checkStatus = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/google/status.php?siteId=${selectedSite.id}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus(result.data);

        if (result.data.connected && result.data.property_selected) {
          fetchAnalytics();
        } else if (result.data.connected && !result.data.property_selected) {
          fetchProperties();
        }
      } else {
        setError(result.message || 'Failed to check status');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const connectGoogle = async () => {
    if (!selectedSite) return;

    setConnecting(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/google/connect.php?siteId=${selectedSite.id}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success && result.auth_url) {
        // Redirect to Google OAuth
        window.location.href = result.auth_url;
      } else {
        setError(result.message || 'Failed to initiate connection');
        setConnecting(false);
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
      setConnecting(false);
    }
  };

  const disconnectGoogle = async () => {
    if (!selectedSite) return;

    if (!window.confirm('Are you sure you want to disconnect Google Analytics?')) {
      return;
    }

    try {
      const response = await fetch('/app/api/google/disconnect.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ siteId: selectedSite.id })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus(null);
        setData(null);
        setProperties([]);
        checkStatus();
      } else {
        setError(result.message || 'Failed to disconnect');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    }
  };

  const fetchProperties = async () => {
    if (!selectedSite) return;

    try {
      const response = await fetch(
        `/app/api/google/properties.php?siteId=${selectedSite.id}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setProperties(result.data.properties || []);
        setShowPropertySelect(true);
      } else {
        setError(result.message || 'Failed to fetch properties');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    }
  };

  const selectProperty = async (propertyId) => {
    if (!selectedSite) return;

    try {
      const response = await fetch('/app/api/google/select-property.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ siteId: selectedSite.id, propertyId })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setShowPropertySelect(false);
        checkStatus();
      } else {
        setError(result.message || 'Failed to select property');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    }
  };

  const fetchAnalytics = async () => {
    if (!selectedSite) return;

    setLoading(true);

    try {
      const response = await fetch(
        `/app/api/google/analytics.php?siteId=${selectedSite.id}&dateRange=${dateRange}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to fetch analytics');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    if (status?.connected && status?.property_selected) {
      fetchAnalytics();
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile': return <Smartphone className="h-5 w-5" />;
      case 'tablet': return <Tablet className="h-5 w-5" />;
      default: return <Monitor className="h-5 w-5" />;
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading Google Analytics...</p>
        </div>
      </div>
    );
  }

  if (!selectedSite) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-yellow-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <p className="text-yellow-800">Please select a site</p>
        </div>
      </div>
    );
  }

  // Not connected state
  if (!status?.connected) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart2 className="h-8 w-8 text-blue-600" />
              Google Analytics
            </h1>
            <p className="text-gray-600 mt-2">Connect your Google Analytics account to view real analytics data</p>
          </div>

          {error && (
            <div className="mb-6 rounded-lg bg-red-50 p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <BarChart2 className="h-10 w-10 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Connect Google Analytics</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Link your Google Analytics account to view traffic, user behavior, and conversion data directly in MageGuard.
            </p>

            {!status?.oauth_configured ? (
              <div className="bg-yellow-50 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">OAuth Not Configured</p>
                    <p className="mt-1">
                      To enable Google Analytics, add your Google OAuth credentials to <code className="bg-yellow-100 px-1 rounded">config.php</code>:
                    </p>
                    <ol className="mt-2 list-decimal ml-4 space-y-1">
                      <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                      <li>Create OAuth 2.0 credentials</li>
                      <li>Add the Client ID and Secret to config.php</li>
                      <li>Enable the Google Analytics Data API</li>
                    </ol>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={connectGoogle}
                disabled={connecting}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {connecting ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Link2 className="h-5 w-5" />
                    Connect with Google
                  </>
                )}
              </button>
            )}

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                We only request read-only access to your analytics data. You can disconnect at any time.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Property selection
  if (showPropertySelect || (status?.connected && !status?.property_selected)) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart2 className="h-8 w-8 text-blue-600" />
              Select Property
            </h1>
            <p className="text-gray-600 mt-2">Choose which Google Analytics property to display</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-green-700 font-medium">Connected to Google</span>
            </div>

            {properties.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 mb-4">Select a GA4 property:</p>
                {properties.map((prop) => (
                  <button
                    key={prop.property_id}
                    onClick={() => selectProperty(prop.property_id)}
                    className="w-full text-left p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{prop.display_name}</div>
                    <div className="text-sm text-gray-500">{prop.account_name}</div>
                    <div className="text-xs text-gray-400 mt-1">{prop.property_id}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No GA4 properties found.</p>
                <p className="text-sm mt-2">Make sure you have GA4 properties in your account.</p>
              </div>
            )}

            <div className="mt-6 pt-4 border-t flex justify-between">
              <button
                onClick={disconnectGoogle}
                className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
              >
                <Unlink className="h-4 w-4" />
                Disconnect
              </button>
              <button
                onClick={fetchProperties}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh List
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main analytics view
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart2 className="h-8 w-8 text-blue-600" />
              Google Analytics
            </h1>
            <p className="text-gray-600 mt-1">
              Property: {data?.property_id?.replace('properties/', '')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Date Range */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              {['1d', '7d', '30d', '90d'].map((range) => (
                <button
                  key={range}
                  onClick={() => handleDateRangeChange(range)}
                  className={`px-3 py-2 text-sm ${
                    dateRange === range
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
            <button
              onClick={fetchAnalytics}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowPropertySelect(true)}
              className="p-2 text-gray-500 hover:text-gray-700"
              title="Change Property"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            {/* Overview Cards */}
            {data.overview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-gray-500">Active Users</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {data.overview.active_users?.toLocaleString()}
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-gray-500">Pageviews</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {data.overview.pageviews?.toLocaleString()}
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                    <span className="text-sm text-gray-500">Sessions</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {data.overview.sessions?.toLocaleString()}
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-5 w-5 text-orange-600" />
                    <span className="text-sm text-gray-500">Avg. Duration</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatDuration(data.overview.avg_session_duration)}
                  </p>
                </div>
              </div>
            )}

            {/* Secondary Stats */}
            {data.overview && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow p-4">
                  <p className="text-sm text-gray-500">New Users</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {data.overview.new_users?.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <p className="text-sm text-gray-500">Bounce Rate</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {data.overview.bounce_rate}%
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <p className="text-sm text-gray-500">Engaged Sessions</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {data.overview.engaged_sessions?.toLocaleString()}
                  </p>
                </div>
              </div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Pages */}
              {data.pages && data.pages.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Pages</h3>
                  <div className="space-y-3">
                    {data.pages.map((page, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{page.path}</p>
                        </div>
                        <div className="ml-4 text-right">
                          <p className="text-sm font-semibold text-gray-900">{page.pageviews.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">{formatDuration(page.avg_time)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Traffic Sources */}
              {data.sources && data.sources.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Traffic Sources</h3>
                  <div className="space-y-3">
                    {data.sources.map((source, idx) => {
                      const total = data.sources.reduce((sum, s) => sum + s.sessions, 0);
                      const percentage = total > 0 ? (source.sessions / total) * 100 : 0;
                      return (
                        <div key={idx}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">{source.channel}</span>
                            <span className="text-sm text-gray-600">{source.sessions.toLocaleString()}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Countries */}
              {data.countries && data.countries.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Globe className="h-5 w-5 text-blue-600" />
                    Top Countries
                  </h3>
                  <div className="space-y-2">
                    {data.countries.map((country, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <span className="text-sm text-gray-900">{country.country}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-gray-900">{country.users.toLocaleString()}</span>
                          <span className="text-xs text-gray-500 ml-2">users</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cities */}
              {data.cities && data.cities.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-purple-600" />
                    Top Cities
                  </h3>
                  <div className="space-y-2">
                    {data.cities.map((city, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <span className="text-sm text-gray-900">{city.city}</span>
                          <span className="text-xs text-gray-500 ml-2">({city.country})</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-gray-900">{city.users.toLocaleString()}</span>
                          <span className="text-xs text-gray-500 ml-2">users</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Devices */}
              {data.devices && data.devices.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Devices</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {data.devices.map((device, idx) => {
                      const total = data.devices.reduce((sum, d) => sum + d.users, 0);
                      const percentage = total > 0 ? Math.round((device.users / total) * 100) : 0;
                      return (
                        <div key={idx} className="text-center p-4 bg-gray-50 rounded-lg">
                          <div className="flex justify-center mb-2 text-gray-600">
                            {getDeviceIcon(device.device)}
                          </div>
                          <p className="text-2xl font-bold text-gray-900">{percentage}%</p>
                          <p className="text-sm text-gray-500">{device.device}</p>
                          <p className="text-xs text-gray-400">{device.users.toLocaleString()} users</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Disconnect Option */}
            <div className="flex justify-end">
              <button
                onClick={disconnectGoogle}
                className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
              >
                <Unlink className="h-4 w-4" />
                Disconnect Google Analytics
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleAnalytics;
